import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  formatDroppedMentionsMessage,
  mergeMessageDelta,
  PROJECT_MESSAGE_POLL_MS,
  type ProjectMessage,
  type ProjectMessagesPage,
  type ProjectMessageDelta,
  type MessageAttachmentInput,
  type MentionableUser,
} from '@task-tracker/shared';
import { apiClient } from '../lib/api-client';
import { documentKeys } from './use-documents';

/**
 * Query keys.
 *
 * TanStack compares key elements POSITIONALLY, so every key here carries a
 * distinct second segment and invalidations name an exact key. Never invalidate
 * the bare 'project-messages' prefix: it is shared by the transcript, which is
 * mutated directly via setQueryData and would be thrown away mid-poll.
 */
export const projectMessageKeys = {
  root: () => ['project-messages'] as const,
  list: (projectId: string) => ['project-messages', 'list', projectId] as const,
  unread: (projectId: string) => ['project-messages', 'unread', projectId] as const,
  mentionable: (projectId: string, q: string) =>
    ['project-messages', 'mentionable', projectId, q] as const,
};

/** The transcript plus the cursor the next poll must use. */
interface TranscriptCache {
  messages: ProjectMessage[];
  has_more: boolean;
  /** ALWAYS a server-issued value. See useProjectMessages. */
  cursor: string;
}

function warnDroppedMentions(dropped: string[] | undefined) {
  if (dropped?.length) toast.warning(formatDroppedMentionsMessage(dropped));
}

// ── Transcript + poll ────────────────────────────────────────────────────────

/**
 * Load a project's transcript, then keep it fresh by polling for a delta.
 *
 * `enabled` is the Messages tab's mounted state. Every open tab is a Worker
 * invocation plus one query every 10 s, so a tab the user is not looking at
 * must not poll — that, plus `refetchIntervalInBackground: false`, is what
 * keeps the cost of this feature proportional to actual use.
 *
 * The delta is MERGED into the cache rather than replacing it: replacing would
 * reset scroll position on every tick and force the server to re-send the whole
 * transcript instead of what changed.
 */
export function useProjectMessages(projectId: string, options: { enabled: boolean }) {
  const queryClient = useQueryClient();
  const key = projectMessageKeys.list(projectId);

  const transcript = useQuery<TranscriptCache>({
    queryKey: key,
    queryFn: async () => {
      const res = await apiClient<ProjectMessagesPage>(`/api/v1/projects/${projectId}/messages`);
      const page = res.data;
      return { messages: page.messages, has_more: page.has_more, cursor: page.server_time };
    },
    enabled: options.enabled && !!projectId,
    // The poll below is the freshness mechanism; refetching the whole
    // transcript on focus would undo the point of the delta.
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const cursor = transcript.data?.cursor;

  useQuery({
    queryKey: ['project-messages', 'poll', projectId, cursor],
    queryFn: async () => {
      if (!cursor) return null;
      const { data: delta } = await apiClient<ProjectMessageDelta>(
        `/api/v1/projects/${projectId}/messages/poll?since=${encodeURIComponent(cursor)}`,
      );

      queryClient.setQueryData<TranscriptCache>(key, (prev) => {
        if (!prev) return prev;
        // A truncated delta is a PREFIX of what changed, so advancing over it
        // would skip a window. Refetch the transcript instead.
        if (delta.truncated) {
          void queryClient.invalidateQueries({ queryKey: key });
          return prev;
        }
        return {
          ...prev,
          messages: mergeMessageDelta(prev.messages, delta.messages),
          // The server's cursor, never `new Date()`. The server derives it from
          // the rows it just sent, on the database's clock; substituting the
          // browser's would silently skip messages under any clock skew.
          cursor: delta.server_time,
        };
      });

      return delta;
    },
    enabled: options.enabled && !!projectId && !!cursor,
    refetchInterval: PROJECT_MESSAGE_POLL_MS,
    // A background tab must not poll — it is a Worker invocation per tick.
    refetchIntervalInBackground: false,
    gcTime: 0,
    staleTime: 0,
  });

  return {
    messages: transcript.data?.messages ?? [],
    hasMore: transcript.data?.has_more ?? false,
    isLoading: transcript.isLoading,
    error: transcript.error,
  };
}

// ── Unread badge ─────────────────────────────────────────────────────────────

export function useProjectMessagesUnread(projectId: string, enabled = true) {
  return useQuery({
    queryKey: projectMessageKeys.unread(projectId),
    queryFn: () => apiClient<{ count: number }>(`/api/v1/projects/${projectId}/messages/unread`),
    enabled: enabled && !!projectId,
  });
}

/** Move the read watermark. Fires when the tab mounts and again as messages
 *  arrive while it is open, so the badge cannot reappear behind the user. */
export function useMarkMessagesRead(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiClient(`/api/v1/projects/${projectId}/messages/read`, { method: 'POST' }),
    onSuccess: () => {
      // Matches the ApiResponse envelope the query itself stores — a bare
      // { count: 0 } would read back as undefined and only look correct.
      queryClient.setQueryData(projectMessageKeys.unread(projectId), { data: { count: 0 } });
      // The count also rides on the project-detail response.
      void queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });
}

/** Mark read on mount and whenever the newest message changes while mounted. */
export function useAutoMarkRead(
  projectId: string,
  active: boolean,
  newestMessageId: string | undefined,
) {
  const markRead = useMarkMessagesRead(projectId);
  const mutate = markRead.mutate;

  useEffect(() => {
    if (!active || !projectId) return;
    mutate();
  }, [active, projectId, newestMessageId, mutate]);
}

// ── Mutations ────────────────────────────────────────────────────────────────

export function useSendMessage(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      id: string;
      content?: string;
      parent_message_id?: string;
      attachments?: MessageAttachmentInput[];
    }) =>
      apiClient<{ message: ProjectMessage }>(`/api/v1/projects/${projectId}/messages`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: ({ data: { message } }) => {
      warnDroppedMentions(message.droppedMentions);
      // Insert straight away rather than waiting up to a full poll interval to
      // see your own message.
      queryClient.setQueryData<TranscriptCache>(projectMessageKeys.list(projectId), (prev) =>
        prev ? { ...prev, messages: mergeMessageDelta(prev.messages, [message]) } : prev,
      );
      // An attachment is mirrored into Documents & Forms, so that list is stale.
      if (message.attachments.length > 0) {
        void queryClient.invalidateQueries({ queryKey: documentKeys.list(projectId) });
      }
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to send message.'),
  });
}

export function useEditMessage(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; content: string; version: number }) =>
      apiClient<{ message: ProjectMessage }>(`/api/v1/messages/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: ({ data: { message } }) => {
      warnDroppedMentions(message.droppedMentions);
      queryClient.setQueryData<TranscriptCache>(projectMessageKeys.list(projectId), (prev) =>
        prev ? { ...prev, messages: mergeMessageDelta(prev.messages, [message]) } : prev,
      );
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to edit message.'),
  });
}

export function useDeleteMessage(projectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient(`/api/v1/messages/${id}`, { method: 'DELETE' }),
    onSuccess: (_data, id) => {
      // Tombstone locally so the message stops rendering immediately; the poll
      // delivers the authoritative tombstone on its next tick.
      queryClient.setQueryData<TranscriptCache>(projectMessageKeys.list(projectId), (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: prev.messages.map((m) =>
            m.id === id
              ? {
                  ...m,
                  content: null,
                  attachments: [],
                  reactions: [],
                  mentions: [],
                  deleted_at: new Date().toISOString(),
                }
              : m,
          ),
        };
      });
      void queryClient.invalidateQueries({ queryKey: documentKeys.list(projectId) });
    },
    onError: (error: Error) => toast.error(error.message || 'Failed to delete message.'),
  });
}

/**
 * Toggle a reaction, optimistically.
 *
 * Optimistic because a reaction is the one interaction where a 10 s wait for
 * the next poll would feel broken. On failure the previous transcript is
 * restored wholesale rather than un-toggling by hand — the poll may have
 * merged other changes in the meantime, and reconstructing the prior state
 * field-by-field would be guesswork.
 */
export function useToggleReaction(projectId: string) {
  const queryClient = useQueryClient();
  const key = projectMessageKeys.list(projectId);

  return useMutation({
    mutationFn: ({ id, emoji, reacted }: { id: string; emoji: string; reacted: boolean }) =>
      reacted
        ? apiClient(`/api/v1/messages/${id}/reactions/${encodeURIComponent(emoji)}`, {
            method: 'DELETE',
          })
        : apiClient(`/api/v1/messages/${id}/reactions`, {
            method: 'POST',
            body: JSON.stringify({ emoji }),
          }),
    onMutate: async ({ id, emoji, reacted }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<TranscriptCache>(key);

      queryClient.setQueryData<TranscriptCache>(key, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: prev.messages.map((m) => {
            if (m.id !== id) return m;
            const groups = m.reactions.filter((r) => r.emoji !== emoji);
            const existing = m.reactions.find((r) => r.emoji === emoji);
            const count = (existing?.count ?? 0) + (reacted ? -1 : 1);
            return {
              ...m,
              reactions:
                count > 0
                  ? [...groups, { emoji, count, reacted_by_me: !reacted }].sort(
                      (a, b) => b.count - a.count || a.emoji.localeCompare(b.emoji),
                    )
                  : groups,
            };
          }),
        };
      });

      return { previous };
    },
    onError: (error: Error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
      toast.error(error.message || 'Failed to update reaction.');
    },
  });
}

// ── Attachments ──────────────────────────────────────────────────────────────

/**
 * Upload a file and return the descriptor to hand to `useSendMessage`.
 *
 * The message row does not exist yet — the client generated its id — so the
 * upload URL is minted against the project. The descriptor is then sent WITH
 * the message rather than confirmed afterwards, because
 * `project_message_attachments.message_id` is a NOT NULL FK: there is no order
 * in which a separate confirm could succeed.
 */
export function useUploadMessageAttachment(projectId: string) {
  return useMutation({
    mutationFn: async ({
      messageId,
      file,
    }: {
      messageId: string;
      file: File;
    }): Promise<MessageAttachmentInput> => {
      const {
        data: { uploadUrl, storagePath },
      } = await apiClient<{
        uploadUrl: string;
        storagePath: string;
      }>(
        `/api/v1/projects/${projectId}/messages/${messageId}/attachments/upload-url?filename=${encodeURIComponent(file.name)}`,
        { method: 'POST' },
      );

      const res = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      if (!res.ok) throw new Error('Upload failed. Please try again.');

      return {
        storage_path: storagePath,
        filename: file.name,
        mime_type: file.type,
        file_size: file.size,
      };
    },
    onError: (error: Error) => toast.error(error.message || 'Upload failed.'),
  });
}

// ── Mentionable users ────────────────────────────────────────────────────────

export function useMessageMentionableUsers(projectId: string, q: string, enabled: boolean) {
  return useQuery({
    queryKey: projectMessageKeys.mentionable(projectId, q),
    queryFn: () =>
      apiClient<{ users: MentionableUser[] }>(
        `/api/v1/projects/${projectId}/messages/mentionable-users${q ? `?q=${encodeURIComponent(q)}` : ''}`,
      ),
    enabled: enabled && !!projectId,
    staleTime: 60_000,
  });
}
